import { EventEmitter, Injectable, signal } from '@angular/core';
import {
  Activity,
  MessageBus,
  NewClaim,
  NotificationItem,
  SearchResult,
} from 'src/app/shared';
import {
  AlertActionType,
  SaveAlertActionRequest,
} from 'src/app/shared/models/alert';
import { Http } from 'src/app/shared/services/http.service';
import { UserActionService } from '../../shared';
import { ActivityRequest } from '../../shared/models/activity';
import { Restriction } from '../components/activity/restriction-detail.component';
import { LogService } from './log.service';

@Injectable({
  providedIn: 'root',
})
export class ActivityService {
  alertActions = signal<SaveAlertActionRequest[]>([]);

  alertSelected = new EventEmitter<string>();
  absenceDeleted = new EventEmitter<string>();
  notificationCount = new EventEmitter<number>();
  focusNotification = new EventEmitter<string>();

  constructor(
    private http: Http,
    private userAction: UserActionService,
    private messageBus: MessageBus,
    public logService: LogService
  ) {}

  async postActivities(path: string, activityRequest: ActivityRequest) {
    return this.http.post<NotificationItem[]>(path, activityRequest, {
      cache: this.userAction.getBustCache() ? false : true,
    });
  }

  async getActivities(path: string, activityRequest: ActivityRequest) {
    return this.http.post<Activity>(path, activityRequest, {
      cache: this.userAction.getBustCache() ? false : true,
    });
  }

  async saveAlertAction(request: SaveAlertActionRequest) {
    const actions = this.alertActions();
    this.alertActions.set([...actions, request]);
    this.messageBus.publish('alert-action', request);
    const claim: SearchResult = <SearchResult>{};

    claim.claimId = request.claimId;
    claim.source = request.sourceSystem;
    claim.userOwns = request.userOwns;
    claim.lob = request.lob;

    const type = !request.type ? actions[0]?.type : request.type;

    if (claim.claimId != null && claim.claimId != '') {
      return this.http.post<number>(
        '/api/alert',
        request,
        null,
        type,
        'Submit - ' + type,
        claim
      );
    } else {
      return this.http.post<number>(
        '/api/alert',
        request,
        null,
        null,
        null,
        claim
      );
    }
  }

  async DismissNotificationAlert(
    triggerType: string,
    claimNumber: string,
    claimId: string,
    claimSource: string,
    userOwns: boolean,
    lob: string,
    type?: AlertActionType
  ) {
    if (
      sessionStorage.getItem('triggerKey') !== null &&
      sessionStorage.getItem('alertMasterId') !== null
    ) {
      const alertAction: SaveAlertActionRequest = {
        action: 'DISMISSED - VOE',
        alertMasterUid: Number(sessionStorage.getItem('alertMasterId')),
        sourceSystem: claimSource,
        claimNumber: claimNumber.replace(' - ', ''),
        claimId: claimId,
        userOwns: userOwns,
        type: type,
        lob: lob,
      };

      await this.saveAlertAction(alertAction);
      sessionStorage.removeItem('triggerKey');
      sessionStorage.removeItem('alertMasterId');
    }
    this.userAction.setBustCache(true);
  }

  filterNewClaimAlerts(newClaims: NewClaim[], alertSelected: string) {
    return newClaims.filter((item) => item.alertId !== alertSelected);
  }

  filterNotifications(
    notifications: NotificationItem[],
    alertSelected: string
  ) {
    const tempNotifications = notifications.filter(
      (item) => item.alertId !== alertSelected
    );
    const tempLength = Number(tempNotifications.length);
    this.notificationCount.emit(tempLength >= 99 ? 99 : tempLength);

    return tempNotifications;
  }

  async getRestrictions(
    source: string,
    claimId: string,
    lob: string
  ): Promise<Restriction> {
    return await this.http.get<Restriction>(
      `/api/restriction/${source}/${claimId}/${lob}/restrictions`
    );
  }

  async getWorkStatusInfo(
    source: string,
    claimId: string,
    lob: string
  ): Promise<WorkStatus[]> {
    return await this.http.get<WorkStatus[]>(
      `/api/claim/${source}/${claimId}/${lob}/WorkStatus`
    );
  }

  setFocusToNotification(index?: string) {
    const tab = index != null ? index : '';
    this.focusNotification.emit(tab);
  }
}

class WorkStatus {
  workStatus?: string;
  duration?: string;
  benefitBegin?: Date;
  benefitEnd?: Date;
  type?: string;
  benefitStatus?: string;
}

